import moment from 'moment'
import { flatten, map, path, pipe } from 'ramda'
import { useContext } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import CurrencyFormatter from '@/components/currency-formatter'
import { Input, Label, Row, Group, FieldDropdown } from '@/components/form'
import Icon from '@/components/icon'
import config from '@/config'
import { show as showProcessingFeesModal } from '@/features/Payments/processing-fees-modal'
import useMediaQuery from '@/hooks/use-media-query'
import { getFees } from '@/models/payment/ports'
import { breakpoints, colors } from '@/styles'
import { ErrorText } from '@/components/form/error'
import AreaTitle from '@/components/area-title'
import { show as showDiscountCodeModal } from './discount-code-modal'
import { StyledPanel, StyledRow, StyledLabel, StyledValue, FeeInfoWrapper, Link } from '../elements'
import Context from '../context'

const { WEB_URL } = config

const StyledLink = styled(StyledLabel)`
  color: ${colors.link};
  cursor: pointer;
  :hover {
    opacity: 0.8;
  }
`

const FeeText = styled.p`
  font-size: 12px;
  letter-spacing: -0.2px;
  line-height: 17px;
  color: ${colors.mediumGrey};
  margin: 0;
`

const FeeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
`

const FeeValue = styled(StyledValue)`
  margin-bottom: 25px;
`

const WarningText = styled(FeeText)`
  color: ${colors.primary};
  margin: 10px 0;
`

const InfoTextWrapper = styled.div`
  border-radius: 10px;
  background-color: #e9f2fc;
  padding: 20px 35px;
  margin: 35px 0 0;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 20px 25px;
  }
`

const InfoText = styled.p`
  font-size: 12px;
  letter-spacing: -0.2px;
  line-height: 17px;
  text-align: center;
  margin: 0;
`

const PaymentMethodForm = ({ values, setFieldValue, acceptedTerms, setAcceptedTerms }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const {
    feesInfo,
    setFeesInfo,
    paymentData: { description, due_date, requested_amount, user_id },
    setPaymentMethod
  } = useContext(Context)

  const { listingId } = useParams()
  const method = values.payment_method
  const discount = path([method, 'discount'], feesInfo)
  const discountAllowed = path([method, 'discount_allowed'], feesInfo)
  const hasPromoCode = !!values.promo_code
  const feeAmount = path([method, 'fee'], feesInfo)
  const totalAmount = path([method, 'charge'], feesInfo)
  const isBitcoinPayment = values.payment_method === 'crypto'
  const companyId = useSelector(path(['listing', listingId, 'users', user_id, 'company_id']))
  const acceptedPaymentMethods = useSelector(
    path(['listing', listingId, 'companies', companyId, 'ach_connected'])
  )

  const paymentMethodOptions = pipe(
    map(option => {
      switch (option) {
        case 'upop':
          return {
            value: 'crypto',
            label: t('r.payment.edit.payment_method_bitcoin.label', 'Bitcoin')
          }
        case 'stripe':
          return {
            value: 'stripe',
            label: t(
              'r.payment.edit.payment_method_visa_master.label',
              'Visa/Mastercard/Amex/UnionPay'
            )
          }
        default:
          return []
      }
    }),
    flatten
  )(acceptedPaymentMethods || [])

  const getFeeText = () => {
    const { percentage_fee, fixed_fee } = path([method, 'ach_info'], feesInfo)
    if (values.payment_method === 'stripe') return '~5.83% + $0.30'
    if (values.payment_method === 'crypto') return '~0.51%'
    return `${percentage_fee}%${fixed_fee > 0 ? ` + $${Number(fixed_fee).toFixed(2)}` : ''}`
  }

  const handleRemoveDiscount = async () => {
    const { body, response } = await getFees({
      body: {
        amount: requested_amount
      }
    })
    if (response.ok) {
      setFieldValue('promo_code', '')
      setFeesInfo(body)
    }
  }

  if (!feesInfo) return null

  return (
    <>
      <AreaTitle>{t('r.payment.edit.payment_method.subtitle', 'Payment Method')}</AreaTitle>
      <StyledPanel hSpace={32} vSpace={32}>
        <StyledLabel bottom={16}>
          {t('r.payment.edit.select_payment_method.title', 'Select Payment Method')}
        </StyledLabel>
        <Row bottom={10}>
          <Group>
            <FieldDropdown
              id="payment_method"
              name="payment_method"
              options={paymentMethodOptions}
              placeholder={t(
                'r.payment.edit.make_payment_select_payment_method.label',
                'Select payment method'
              )}
              required
              onChange={setPaymentMethod}
            />
          </Group>
        </Row>
        <StyledRow bottom={10} paddingTop={30} borderTop>
          <StyledLabel>{t('r.payment.edit.payment_detail.label', 'Detail')}</StyledLabel>
          <StyledValue>{description}</StyledValue>
        </StyledRow>
        <StyledRow bottom={10}>
          <StyledLabel>{t('r.payment.edit.make_payment_due_date.label', 'Due date')}</StyledLabel>
          <StyledValue>{moment(due_date).format('MM-DD-YYYY')}</StyledValue>
        </StyledRow>
        <StyledRow bottom="0">
          <StyledLabel>
            {t('r.payment.edit.make_payment_payment_amount.label', 'Payment amount')}
          </StyledLabel>
          <StyledValue>
            <CurrencyFormatter value={requested_amount} showCents />
          </StyledValue>
        </StyledRow>
        {!!values.payment_method && (
          <>
            {hasPromoCode ? (
              <>
                <StyledRow top={10}>
                  <StyledLabel>{t('r.payment.edit.discount.label', 'Discount')}</StyledLabel>
                  <StyledValue data-testid="discount-amount">
                    -<CurrencyFormatter value={discount} showCents />
                  </StyledValue>
                </StyledRow>
                <StyledRow bottom={20}>
                  <div>
                    <StyledLink role="button" onClick={handleRemoveDiscount} as="a">
                      {t('r.payment.edit.remove_discount.button', 'Remove discount')}
                    </StyledLink>
                    {!discountAllowed && (
                      <ErrorText role="alert" left={6}>
                        {t(
                          'r.payment.edit.invalid_discount_code_for_method.error',
                          '** The discount code used is not valid with the selected payment method.'
                        )}
                      </ErrorText>
                    )}
                  </div>
                </StyledRow>
              </>
            ) : (
              <StyledRow bottom={20}>
                <StyledLink
                  role="button"
                  as="a"
                  onClick={() =>
                    showDiscountCodeModal({
                      requested_amount,
                      setFieldValue,
                      setFeesInfo
                    })
                  }
                >
                  + {t('r.payment.edit.add_discount_code.button', 'Add discount code')}
                </StyledLink>
              </StyledRow>
            )}
            <StyledRow bottom={10} paddingTop={30} borderTop>
              <StyledLabel>{t('r.payment.edit.subtotal.label', 'Subtotal')}</StyledLabel>
              <StyledValue data-testid="subtotal-amount">
                <CurrencyFormatter value={requested_amount - (discount || 0)} showCents />
              </StyledValue>
            </StyledRow>
            <StyledRow bottom={20}>
              <FeeWrapper>
                <FeeInfoWrapper>
                  <Label bottom="0" noGutter={displayMobile}>
                    {t('r.payment.edit.make_payment_processing_fees.text', 'Processing fees')}
                  </Label>
                  <Icon id="info_blue" width={16} height={16} onClick={showProcessingFeesModal} />
                </FeeInfoWrapper>
                <FeeText data-testid="fee-text">({getFeeText()})</FeeText>
              </FeeWrapper>
              <FeeValue data-testid="fee-amount">
                <CurrencyFormatter value={feeAmount} showCents />
              </FeeValue>
            </StyledRow>
            <StyledRow paddingTop={30} borderTop>
              <StyledLabel bottom="0" bold>
                {t('r.payment.edit.total.label', 'Total')}
              </StyledLabel>
              <StyledValue bottom="0" data-testid="total-amount">
                <CurrencyFormatter value={totalAmount} showCents />
              </StyledValue>
            </StyledRow>
            {isBitcoinPayment && (
              <>
                <WarningText>
                  {t(
                    'r.payment.edit.bitcoin_fees_notice.text',
                    '**At the next step, the actual amount will be exchanged into Bitcoin, based on the exchange rate at the time. A network fee may be charged by your personal wallet provider.'
                  )}
                </WarningText>
                <StyledRow bottom="0" paddingTop={30}>
                  <Trans i18nKey="b.settings.edit.read_and_agree_to_terms.label">
                    <StyledLabel>
                      I have read and agree to liv.rent{`'`}s{' '}
                      <Link href={`${WEB_URL}/terms`} target="_blank" rel="noopener noreferrer">
                        Terms & Conditions.
                      </Link>
                    </StyledLabel>
                  </Trans>
                  <Input
                    data-cy="agree_to_terms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={e => setAcceptedTerms(e.target.checked)}
                  />
                </StyledRow>
              </>
            )}
          </>
        )}
      </StyledPanel>
      {isBitcoinPayment && (
        <InfoTextWrapper>
          <InfoText>
            {t(
              'r.payment.edit.payment_redirect_info.text',
              'A new window will open to redirect you to our payment service provider. Not being redirected? Try disabling your pop up blocker. Once the transaction has been confirmed, you’ll be redirected back to liv.rent.'
            )}
          </InfoText>
        </InfoTextWrapper>
      )}
    </>
  )
}

export default PaymentMethodForm
